import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { LicenseTier } from '@tokenforge/shared';
import type { AppRole } from './roles';

interface SessionResponse {
  user: {
    id: string;
    name: string;
    email: string;
    role: AppRole;
    departmentIds: string[];
    primaryDepartmentId?: string | null;
    defaultProjectId?: string | null;
  };
  tenant: { id: string; name: string; licenseTier: LicenseTier };
  scopes: string[];
}

interface AuthState {
  user: SessionResponse['user'] | null;
  tenant: SessionResponse['tenant'] | null;
  role: SessionResponse['user']['role'] | null;
  scopes: string[];
  defaultProjectId: string | null;
  licenseTier: LicenseTier;
  isLoading: boolean;
}

const AuthContext = React.createContext<AuthState | null>(null);

function readBrowserPathname(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [pathname, setPathname] = React.useState(readBrowserPathname);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => setPathname(readBrowserPathname());
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = ((...args) => {
      originalPushState(...args);
      window.dispatchEvent(new Event('locationchange'));
    }) as History['pushState'];

    window.history.replaceState = ((...args) => {
      originalReplaceState(...args);
      window.dispatchEvent(new Event('locationchange'));
    }) as History['replaceState'];

    window.addEventListener('popstate', update);
    window.addEventListener('locationchange', update);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', update);
      window.removeEventListener('locationchange', update);
    };
  }, []);

  const isSaasInternalRoute =
    pathname.startsWith('/saas') ||
    pathname.startsWith('/saas-login') ||
    pathname.startsWith('/internal/tenant-debug');
  const { data, isLoading } = useQuery({
    queryKey: ['session', isSaasInternalRoute ? 'disabled' : 'admin'],
    queryFn: () => apiFetch<SessionResponse>('/admin/session'),
    enabled: !isSaasInternalRoute,
    retry: false,
    staleTime: 60_000,
  });
  const value: AuthState = React.useMemo(
    () => ({
      user: data?.user ?? null,
      tenant: data?.tenant ?? null,
      role: data?.user?.role ?? null,
      scopes: data?.scopes ?? [],
      defaultProjectId: data?.user?.defaultProjectId ?? null,
      licenseTier: data?.tenant.licenseTier ?? 'personal',
      isLoading,
    }),
    [data, isLoading],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
